import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import {
    getAddressFormatted,
    getCodeAndDescriptionFormatted,
} from '../../../../../utils/string-utils';
import LoadsConstants from '../../../../../pages/business/loads/constants';
import LoadStatus from '../../../../enums/loads/load-status.enum';
import {
    getFormattedDescription,
    suspensionMessage,
} from '../../../load-card/load-card-body/utils/load-card-body-utils';
import CopyInfo from '../../../copy-info';
import IconInfo from '../../../icon-info';
import LoadType from '../../../../enums/loads/load-type.enum';
import { ReactComponent as TruckIcon } from '../../../../assets/svg/load-card/truck.svg';
import ILoadCardDetailInfo from '../../interfaces/ILoadCardDetailInfo';
import { getLoadDetailsCancellationReasonMessage } from '../../../../utils/cancellation-reason-utils';
import { getUTCDate } from '../../../../../utils/date-utils';

interface IDetailsProps {
    loadData: ILoadCardDetailInfo;
    historyEvents?: Array<{ eventType: string }>;
}

function Details({ loadData, historyEvents }: Readonly<IDetailsProps>) {
    const { t } = useTranslation();

    const originDescription = getAddressFormatted(loadData.routeDetails.source);

    const destinationDescription = getAddressFormatted(
        loadData.routeDetails.destination,
    );

    const winnerDescription = getCodeAndDescriptionFormatted(
        loadData.winnerInfo?.carrierCode,
        loadData.winnerInfo?.carrierName,
        LoadsConstants.NO_INFO_TEXT,
    );

    const isTmsConfirmed: boolean =
        loadData.tagStatus === LoadStatus.ConfirmedTms;

    const isLoadSuspended: boolean =
        loadData.tagStatus === LoadStatus.Suspended;

    const isTmsCancelled: boolean =
        loadData.tagStatus === LoadStatus.CancelledTms;

    const isLoadConfirmed: boolean =
        loadData.tagStatus === LoadStatus.Confirmed || isTmsConfirmed;

    const isLoadCancelled: boolean =
        loadData.tagStatus === LoadStatus.Cancelled;

    const suspensionReason = loadData.statusReason;

    const renderSuspensionOrWinnerInfo = (): JSX.Element => {
        if (isLoadSuspended || isTmsCancelled) {
            return renderSuspendedInfo();
        }

        return renderWinnerInfo();
    };

    const renderWinnerInfo = () => {
        return (
            <div
                className="card-loads-body__carrier-info"
                style={{
                    visibility: shouldShowWinnerInfo() ? 'visible' : 'hidden',
                }}
                data-testid="card-loads-body__carrier-info"
            >
                <span className="card-loads-body__destination-icon">
                    {renderCarrierIcon()}
                </span>
                <span className="card-loads-body__routeDetailLabel">
                    {t('Transportadora')}
                </span>
                <span className="card-loads-body__routeDetailLabel-data">
                    {getFormattedDescription(winnerDescription)}
                </span>
            </div>
        );
    };

    const shouldShowWinnerInfo = () => {
        return isLoadConfirmed && !loadData.cardMessage;
    };

    const renderSuspendedInfo = () => {
        const reasonToShow = getLoadDetailsCancellationReasonMessage(
            suspensionReason,
            loadData.channel,
            isLoadCancelled,
            historyEvents,
            t,
            suspensionMessage,
        );

        return (
            <div
                className="card-loads-body__suspension-info"
                style={{
                    visibility: shouldShowSuspendedInfo()
                        ? 'visible'
                        : 'hidden',
                }}
                data-testid="card-loads-body__suspension-info"
            >
                <span className="card-loads-body__suspension-label">
                    {t('Motivo')}:
                </span>
                <span
                    className="card-loads-body__suspension-message"
                    data-testid="card-loads-body__suspension-message"
                >
                    {reasonToShow}
                </span>
            </div>
        );
    };

    const shouldShowSuspendedInfo = () => {
        return (isLoadSuspended || isTmsCancelled) && suspensionReason;
    };

    const renderCarrierIcon = useCallback(() => {
        return <TruckIcon />;
    }, []);

    const getLoadChannelText = useCallback((): string => {
        if (loadData?.channel) {
            return t(loadData.channel);
        }

        return LoadsConstants.NO_INFO_TEXT;
    }, [loadData, t]);

    const getLoadWeight = useCallback((): string => {
        if (loadData?.shipment.weight) {
            return `${loadData.shipment?.weight} ${loadData.shipment.weightUnitMeasure}`;
        }

        return LoadsConstants.NO_INFO_TEXT;
    }, [loadData]);

    const suggestedVehicleDescription = loadData.vehicle.description;
    const chosenVehicleDescription = loadData.winnerInfo?.vehicleDescription;

    return (
        <div
            className="card-detail-details__container"
            data-testid="card-detail-details__container"
        >
            <div className="card-detail-details__body">
                <h2>{t('Detalhes')}</h2>
                <hr />
                <CopyInfo title="Load" info={loadData.idLoad} />
                <CopyInfo
                    title="Pedido"
                    info={
                        loadData.channel === 'Insumos'
                            ? (loadData.sapOrderNumber ?? '')
                            : (loadData.shipmentNumber ?? '')
                    }
                />
                {loadData.shipment.freightOrder && (
                    <CopyInfo
                        title="Freight Order"
                        info={loadData.shipment.freightOrder}
                    />
                )}
                <IconInfo
                    icon="location"
                    title={t('Origem')}
                    info={originDescription}
                />
                <IconInfo
                    icon="map-pin-icon"
                    title={t('Destino')}
                    info={destinationDescription}
                />
                <IconInfo
                    icon="calendar"
                    title="Data"
                    info={
                        loadData.routeDetails.collectionDate &&
                        getUTCDate(
                            loadData.routeDetails.collectionDate,
                        )?.toLocaleDateString()
                    }
                />
                <IconInfo
                    icon="shopping-cart"
                    title="Carga"
                    info={loadData.shipment.material}
                />

                <IconInfo
                    icon="truck-icon"
                    title="CaminhaoSugerido"
                    info={suggestedVehicleDescription}
                />

                {chosenVehicleDescription && (
                    <IconInfo
                        icon="truck-icon"
                        title="CaminhaoEscolhido"
                        info={chosenVehicleDescription}
                    />
                )}
                {renderSuspensionOrWinnerInfo()}
            </div>
            <div className="card-detail-footer">
                <div className="card-detail-loads-body__attribute">
                    {getLoadChannelText()}
                </div>
                <div className="card-detail-loads-body__attribute">
                    {getLoadWeight()}
                </div>
                {loadData.type === LoadType.Dedicated && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="card-detail-loads-body__dedicated"
                    >
                        {t('Dedicated')}
                    </div>
                )}
                {loadData.type === LoadType.SemiDedicated && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="load-detail-semidedicated"
                    >
                        {t('SemiDedicated')}
                    </div>
                )}
                {loadData.shipment.skids && loadData.shipment.skids > 0 && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="card-detail-loads-body__skids"
                    >
                        {loadData.shipment.skids} Plts
                    </div>
                )}
                {loadData.isReturnable && (
                    <div
                        className="card-detail-loads-body__attribute card-detail-loads-body__attribute--returnable"
                        data-testid="load-detail-returnable"
                    >
                        {t('Returnable')}
                    </div>
                )}
                {loadData.type === LoadType.Loyal && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="load-detail-loyal"
                    >
                        {t('Loyal')}
                    </div>
                )}
                {loadData.type === LoadType.OtherModes && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="load-detail-othermodes"
                    >
                        {t('OtherModes')}
                    </div>
                )}
                {loadData.type === LoadType.Hub && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="load-detail-hub"
                    >
                        {t('Hub')}
                    </div>
                )}
                {loadData.type === LoadType.DraftBeer && (
                    <div
                        className="card-detail-loads-body__attribute"
                        data-testid="load-detail-draftbeer"
                    >
                        {t('DraftBeer')}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Details;
